'use client';

import { useCallback, useEffect } from 'react';
import type { Session } from '@fanduel/cpe-um-bundle/account/contract';
import type { Account } from '@fanduel/cpe-um-bundle/account/library';
import { useStore } from '@/store/store';
import { POPUPS } from '@/app/components/popup/constants';
import { getCookieValue, removeCookie, setCookie } from '@/utils/cookies/client';
import { COOKIES } from '@/utils/cookies';
import { UNIFIED_MODULES_ROUTES } from '@/utils/constants';
import { useRouter } from 'next/navigation';
import { getPlatformBridge } from '@/logic/bridges';
import { removeSession } from '@/logic/login/session';
import { getConfig } from '@/utils/config/config';
import { useSessionKeepAlive } from '@/hooks/use-session-keepalive';
import { ddClearUser, ddRumAction, ddSetUser } from '@/utils/datadog';
import { useGlobalContext } from '@/store/contexts/global-context';
import { getPathFromReferrer } from '@/utils/url/url';
import { getPostLoginAction } from '@/utils/post-login-action/post-login-action';
import { jwtDecode } from 'jwt-decode';

type TriggerActionOptions = { externalReferrer?: string; skipRegionSelector?: boolean };

let initialized: boolean = false;
let initializationPromise: Promise<void> | null = null;

let accountManager: typeof Account | null = null;

export const SESSION_TYPE = {
    LOGIN: 'login',
    REGISTER: 'register'
};

export const SESSION_PHASE = {
    INIT: 'init',
    REFRESH: 'refresh'
} as const;

export type SessionPhase = (typeof SESSION_PHASE)[keyof typeof SESSION_PHASE];

export const sessionCreatedToStoreValue = (created: unknown): string | null => {
    if (created instanceof Date) {
        return created.toISOString();
    }
    if (typeof created === 'string') {
        return created;
    }

    return null;
};

const getSessionTokenContext = (): { sessionTokenExpiry?: string; sessionTokenExpired?: boolean } => {
    try {
        const authToken = getCookieValue(COOKIES.AUTH_TOKEN);
        if (!authToken) return {};

        const { exp } = jwtDecode(authToken);
        if (!exp) return {};

        const expiresAtMs = exp * 1000;
        return {
            sessionTokenExpiry: new Date(expiresAtMs).toISOString(),
            sessionTokenExpired: expiresAtMs <= Date.now()
        };
    } catch {
        return {};
    }
};

export const reinitAccountUmSession = async (trigger: 'visibility' | 'interval') => {
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
    }

    if (initializationPromise && !initialized) {
        await initializationPromise;
    }
    if (!accountManager) return;

    let step: 'keepAlive' | 'getSession' = 'keepAlive';
    try {
        await accountManager.keepAlive();

        const loginToken = getCookieValue(COOKIES.LOGIN_TOKEN);
        if (loginToken) {
            step = 'getSession';
            await accountManager.getSessionAsync({ loginToken });
        }

        ddRumAction({
            name: 'force_session_update',
            description: 'Forced session update succeeded',
            context: { trigger, hadLoginToken: Boolean(loginToken) },
            use_case: 'SESSION'
        });
    } catch (error) {
        ddRumAction({
            name: 'force_session_update_failed',
            description: 'Forced session update threw',
            context: {
                trigger,
                step,
                error: error instanceof Error ? error.message : String(error),
                ...getSessionTokenContext()
            },
            use_case: 'SESSION'
        });
    }
};

export const useAccountUm = () => {
    const queuePopup = useStore((s) => s.popup.queuePopup);
    const setIsLoggedIn = useStore((s) => s.userContext.setIsLoggedIn);
    const setBalance = useStore((s) => s.userContext.setBalance);
    const setLastLogin = useStore((state) => state.userContext.session.setLastLogin);
    const setSessionStartTime = useStore((state) => state.userContext.session.setSessionStartTime);
    const setAccountID = useStore((state) => state.userContext.accountSettings.setAccountID);
    const setInitCompleted = useStore((state) => state.userContext.session.setInitCompleted);
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const handleShowLoading = useStore((state) => state.loading.handleShowLoading);
    const handleHideLoading = useStore((state) => state.loading.handleHideLoading);
    const { isAndroidWrapper, isXsellWrapper } = useGlobalContext();
    const router = useRouter();

    const ensureInitialization = async () => {
        if (initializationPromise && !initialized) {
            handleShowLoading();
            await initializationPromise;
            handleHideLoading();
        }
    };

    // Getters
    const getSession = async () => {
        return accountManager?.getSessionAsync('Silent');
    };

    const getSessionWithLoginView = async () => {
        await ensureInitialization();

        return accountManager!.getSessionAsync('Normal');
    };

    const getSessionWithRegisterView = async () => {
        await ensureInitialization();

        return accountManager!.getSessionAsync('NewUser');
    };

    const isSessionValid = async () => {
        if (initializationPromise && !initialized) {
            await initializationPromise;
        }

        return accountManager?.checkValidSession();
    };

    const getUser = async () => accountManager?.getUserAsync('Silent');

    // Logout
    const logOut = async () => {
        await Promise.resolve(accountManager?.clearSession());
        removeSession();
        setSessionStartTime(null);
        setBalance(0);
        setAccountID('');
        ddClearUser();
    };

    const logOutWithModal = async () => {
        queuePopup({ type: POPUPS.LOGOUT });
        await logOut();
    };

    const triggerLogOut = async () => {
        removeCookie(COOKIES.REGION);
        removeCookie(COOKIES.SHOULD_DISPLAY_REGION_SELECTOR);
        localStorage.removeItem(COOKIES.X_GEOCOMPLY_TOKEN);

        const bridge = getPlatformBridge();
        if ((isAndroidWrapper || isXsellWrapper) && bridge.isCompatible()) {
            bridge.exec('logout');
            await logOut();
        } else {
            await logOutWithModal();
        }
    };

    // Session handling
    const syncCookiesUM = async (session: Session) => {
        const config = getConfig();
        const loginToken = await accountManager!.getLoginTokenAsync();
        const { token, expires } = session;

        // TODO: There is an issue regarding expires not accounting for user current timezone
        if (loginToken) {
            setCookie(COOKIES.AUTH_TOKEN, token, expires.valueOf() - Date.now(), '/', config.rootDomain);
            setCookie(COOKIES.LOGIN_TOKEN, loginToken, expires.valueOf() - Date.now(), '/', config.rootDomain);
        }
    };

    const keepSessionAlive = useCallback(async () => {
        return accountManager?.keepAlive();
    }, []);

    const setDDUser = (token: string) => {
        const decodedToken = jwtDecode(token);

        if (decodedToken.sub) {
            ddSetUser(decodedToken.sub);
        }
    };

    const handleSession = async (session: Session | undefined, context: { phase: SessionPhase }) => {
        const loggedIn = useStore.getState().userContext.isLoggedIn;
        if (session) {
            setSessionStartTime(sessionCreatedToStoreValue(session.created));

            const user = await getUser();

            if (user?.userId) {
                setAccountID(user?.userId);
            }

            ddRumAction({
                name: 'session_extended',
                description: 'Session was extended successfully.',
                context: {
                    ...context,
                    sessionId: session.sessionId,
                    expires: session.expires
                },
                use_case: 'SESSION'
            });
            await syncCookiesUM(session);
            const lastLoginDate = (await accountManager?.getPriorSession?.())?.created;
            setLastLogin(lastLoginDate || '');
            setDDUser(session.token);

            // Geolocation effects are handled outside of this hook.

            // On logged-out -> logged-in with a pending CTA action, reload once after cookie sync
            // so the next boot consumes the action from a fully logged-in page.
            if (!loggedIn && context.phase === SESSION_PHASE.REFRESH && getPostLoginAction() !== null) {
                window.location.reload();
                return;
            }
        } else if (loggedIn) {
            //TODO never check  AUTH_TOKEN...
            // Only display if the user was logged in before
            ddRumAction({
                name: 'session_invalidated',
                description: 'Session was invalidated.',
                context,
                use_case: 'SESSION'
            });
            triggerLogOut();
        }
        setIsLoggedIn(Boolean(session));
    };

    // Register
    const buildRefParam = (ref?: string) => (ref ? `?external-referrer-next=${encodeURIComponent(ref)}` : '');

    const triggerRegisterWeb = async (options?: TriggerActionOptions) => {
        const shouldShowRegionSelector =
            getCookieValue(COOKIES.SHOULD_DISPLAY_REGION_SELECTOR) === 'true' && !options?.skipRegionSelector;
        if (shouldShowRegionSelector) {
            queuePopup({
                type: POPUPS.STATE_SELECTOR,
                params: {
                    type: SESSION_TYPE.REGISTER,
                    externalReferrer: options?.externalReferrer
                }
            });
        } else {
            // Read before login completes: post-login action handler clears sessionStorage once logged in.
            const hasPendingPostLoginAction = Boolean(getPostLoginAction());
            const session = await accountManager?.getSessionAsync('NewUser');
            if (!session) {
                return;
            }
            if (hasPendingPostLoginAction) {
                return;
            }
            const path = getPathFromReferrer(options?.externalReferrer);
            if (path) {
                router.push(path);
            }
            router.refresh();
        }
    };

    const triggerRegister = (options?: TriggerActionOptions) => {
        if (isAndroidWrapper) {
            window.location.href = `${UNIFIED_MODULES_ROUTES.REGISTER}${buildRefParam(options?.externalReferrer)}`;
        } else if (isXsellWrapper) {
            const bridge = getPlatformBridge();
            if (bridge.isCompatible()) {
                bridge.exec('join');
            } else {
                triggerRegisterWeb(options);
            }
        } else {
            triggerRegisterWeb(options);
        }
    };

    // Login
    const triggerLoginWeb = async (options?: TriggerActionOptions) => {
        const shouldShowRegionSelector =
            getCookieValue(COOKIES.SHOULD_DISPLAY_REGION_SELECTOR) === 'true' && !options?.skipRegionSelector;
        if (shouldShowRegionSelector) {
            queuePopup({
                type: POPUPS.STATE_SELECTOR,
                params: {
                    type: SESSION_TYPE.LOGIN,
                    externalReferrer: options?.externalReferrer
                }
            });
        } else {
            // Read before login completes: post-login action handler clears sessionStorage once logged in.
            const hasPendingPostLoginAction = Boolean(getPostLoginAction());
            const session = await getSessionWithLoginView();
            if (!session) {
                return;
            }

            ddRumAction({
                name: 'login_success',
                description: 'User logged in successfully.',
                use_case: 'SESSION'
            });
            await syncCookiesUM(session);

            if (hasPendingPostLoginAction) {
                return;
            }

            const path = getPathFromReferrer(options?.externalReferrer);
            if (path) {
                router.push(path);
            }
            router.refresh();
        }
    };

    const triggerLogin = (options?: TriggerActionOptions) => {
        if (isAndroidWrapper) {
            window.location.href = `${UNIFIED_MODULES_ROUTES.LOGIN}${buildRefParam(options?.externalReferrer)}`;
        } else if (isXsellWrapper) {
            const bridge = getPlatformBridge();
            if (bridge.isCompatible()) {
                bridge.exec('login');
            } else {
                triggerLoginWeb(options);
            }
        } else {
            triggerLoginWeb(options);
        }
    };

    // Initialize
    const initializeAccountUM = async () => {
        if (initialized) {
            return;
        }

        if (initializationPromise) {
            await initializationPromise;
            return;
        }

        initializationPromise = (async () => {
            accountManager = (await import('@fanduel/cpe-um-bundle/account/library')).Account;

            const { initializeUnifiedModules } = await import('@/logic/unified-modules');
            await initializeUnifiedModules();

            const loginToken = getCookieValue(COOKIES.LOGIN_TOKEN);
            let session = undefined;

            if (loginToken) {
                session = await accountManager?.getSessionAsync({ loginToken });
            }

            await handleSession(session, {
                phase: SESSION_PHASE.INIT
            });

            setInitCompleted(true);

            await accountManager.subscribeSession((val) =>
                handleSession(val, {
                    phase: SESSION_PHASE.REFRESH
                })
            );

            initialized = true;
        })();

        await initializationPromise;
    };

    useEffect(() => {
        initializeAccountUM();
    }, []);

    useSessionKeepAlive({
        onActivity: keepSessionAlive
    });

    return {
        getSession,
        getSessionWithLoginView,
        getSessionWithRegisterView,
        isSessionValid,
        keepSessionAlive,
        triggerLogin,
        triggerRegister,
        triggerLogOut,
        logOut,
        logOutWithModal,
        getUser,
        isLoggedIn
    };
};
